import * as Yup from "yup";

import { ENV } from "../../../../../utils/constants";

export const API_URL = ENV.SERVER_HOST;

export const INITIAL_VARIANT = {
  sku: "",
  attributes: [],
  price: "",
  offerPrice: "",
  cost: "",
  stock: 0,
  lowStockAlert: 0,
  enabled: true,
  images: [],
};

export function createSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function createProductId(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function createSku(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function createLocalId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `img-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const path = String(imagePath);

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:")
  ) {
    return path;
  }

  const normalizedPath = path.replace(/\\/g, "/").replace(/^\/+/, "");

  return `${API_URL}/${normalizedPath}`;
}

function getRelatedId(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "object") {
    return String(value._id || value.id || "");
  }

  return String(value);
}

function normalizeStringArray(value) {
  if (!value) {
    return [];
  }

  const values = Array.isArray(value) ? value : [value];

  return [
    ...new Set(
      values
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    ),
  ];
}

function getImageReference(image) {
  if (!image) {
    return "";
  }

  return String(
    image.localId ||
      image._id ||
      image.path ||
      ""
  ).trim();
}

function mapProductImages(images = []) {
  return images.map((productImage, index) => {
    const imageId = productImage._id ? String(productImage._id) : "";
    const imagePath = productImage.path || "";

    return {
      localId: imageId || createLocalId(),
      _id: imageId || null,
      file: null,
      path: imagePath,
      url: getImageUrl(imagePath),
      name: productImage.name || "",
      order: Number(productImage.order) || index + 1,
      isCover: Boolean(productImage.isCover),
      existing: true,
    };
  });
}

function createImageReferenceMap(images = []) {
  const referenceMap = new Map();

  images.forEach((image) => {
    const localId = String(image.localId || "").trim();
    const id = String(image._id || "").trim();
    const path = String(image.path || "").trim();

    if (localId) {
      referenceMap.set(localId, localId);
    }

    if (id) {
      referenceMap.set(id, localId || id);
    }

    if (path) {
      referenceMap.set(path, localId || id || path);
    }
  });

  return referenceMap;
}

function mapVariants(variants = [], mappedImages = []) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return [{ ...INITIAL_VARIANT }];
  }

  const imageReferenceMap = createImageReferenceMap(mappedImages);

  const isSingleVariant = variants.length === 1;
  const allImageIds = mappedImages
    .map((image) => image.localId)
    .filter(Boolean);

  return variants.map((variant) => {
    const variantImages = isSingleVariant
      ? allImageIds
      : normalizeStringArray(variant.images || variant.image)
          .map(
            (imageReference) =>
              imageReferenceMap.get(imageReference) || imageReference
          )
          .filter(Boolean);

    return {
      sku: variant.sku || "",
      attributes: Array.isArray(variant.attributes)
        ? variant.attributes.map((attribute) => ({
            schema: attribute.schema || null,
            classification: attribute.classification || null,
            value: attribute.value || "",
            name: attribute.name || "",
            label: attribute.label || attribute.value || "",
          }))
        : [],
      price: variant.price ?? "",
      offerPrice: variant.offerPrice ?? "",
      cost: variant.cost ?? "",
      stock: variant.stock ?? 0,
      lowStockAlert: variant.lowStockAlert ?? 0,
      enabled: variant.enabled !== false,
      images: normalizeStringArray(variantImages),
    };
  });
}

export const initialValues = {
  id: "",
  name: "",
  slug: "",
  category: "",
  brand: "",
  description: "",
  characteristics: [],
  variants: [{ ...INITIAL_VARIANT }],
  visible: true,
  featured: false,
  seo: {
    metaTitle: "",
    metaDescription: "",
  },
  images: [],
  _imageError: "",
};

export function mapProductToForm(product) {
  const images = mapProductImages(
    Array.isArray(product.images) ? product.images : []
  );

  return {
    id: product.id || "",
    name: product.name || "",
    slug: product.slug || "",
    category: getRelatedId(product.category),
    brand: getRelatedId(product.brand),
    description: product.description || "",
    characteristics: Array.isArray(product.characteristics)
      ? product.characteristics.map((characteristic) =>
          getRelatedId(characteristic)
        )
      : [],
    variants: mapVariants(product.variants, images),
    visible: Boolean(product.visible),
    featured: Boolean(product.featured),
    seo: {
      metaTitle: product.seo?.metaTitle || "",
      metaDescription: product.seo?.metaDescription || "",
    },
    images,
    _imageError: "",
  };
}

export function getProductPayload(values) {
  const images = Array.isArray(values.images) ? values.images : [];

  const imageReferenceMap = new Map();

  images.forEach((image) => {
    const localId = String(image.localId || "").trim();
    const id = String(image._id || "").trim();
    const path = String(image.path || "").trim();

    if (localId) {
      imageReferenceMap.set(localId, localId);
    }

    if (id) {
      imageReferenceMap.set(id, localId || id);
    }

    if (path) {
      imageReferenceMap.set(path, localId || id || path);
    }
  });

  const isSingleVariant =
    Array.isArray(values.variants) && values.variants.length === 1;

  const allImageIds = images
    .map((image) => String(image.localId || "").trim())
    .filter(Boolean);

  return {
    id: createProductId(values.id || values.name),
    name: String(values.name || "").trim(),
    slug: createSlug(values.slug || values.name),
    category: values.category || null,
    brand: values.brand || null,
    description: String(values.description || "").trim(),

    characteristics: Array.isArray(values.characteristics)
      ? normalizeStringArray(
          values.characteristics.map((characteristic) =>
            getRelatedId(characteristic)
          )
        )
      : [],

    variants: (values.variants || []).map((variant) => ({
      sku: createSku(variant.sku),

      attributes: Array.isArray(variant.attributes)
        ? variant.attributes
            .map((attribute) => ({
              schema:
                typeof attribute.schema === "object"
                  ? attribute.schema?._id || attribute.schema?.id || null
                  : attribute.schema || null,

              classification:
                typeof attribute.classification === "object"
                  ? attribute.classification?._id ||
                    attribute.classification?.id ||
                    null
                  : attribute.classification || null,

              name: String(attribute.name || "").trim(),
              value: String(attribute.value || "").trim(),
              label: String(attribute.label || "").trim(),
            }))
            .filter((attribute) => attribute.value)
        : [],

      price: Number(variant.price),

      offerPrice:
        variant.offerPrice === "" ||
        variant.offerPrice === null ||
        variant.offerPrice === undefined
          ? null
          : Number(variant.offerPrice),

      cost:
        variant.cost === "" ||
        variant.cost === null ||
        variant.cost === undefined
          ? null
          : Number(variant.cost),

      stock: Number(variant.stock),

      lowStockAlert: Number(variant.lowStockAlert),

      enabled: variant.enabled !== false,

      images: isSingleVariant
        ? allImageIds
        : normalizeStringArray(variant.images)
            .map(
              (imageReference) =>
                imageReferenceMap.get(imageReference) || imageReference
            )
            .filter(Boolean),
    })),

    visible: Boolean(values.visible),

    featured: Boolean(values.featured),

    seo: {
      metaTitle: String(values.seo?.metaTitle || "").trim(),
      metaDescription: String(values.seo?.metaDescription || "").trim(),
    },

    images: images.map((image, index) => ({
      _id: image._id || null,
      localId: String(image.localId || "").trim(),
      path: image.existing ? String(image.path || "").trim() : "",
      name: String(image.name || "").trim(),
      order: Number(image.order) || index + 1,
      isCover: Boolean(image.isCover),
      file: image.file instanceof File ? image.file : undefined,
    })),
  };
}

const variantSchema = Yup.object({
  sku: Yup.string()
    .transform((value) => createSku(value))
    .max(80, "El SKU no puede superar 80 caracteres."),

  attributes: Yup.array().default([]),

  price: Yup.number()
    .typeError("El precio debe ser un número válido.")
    .min(0, "El precio debe ser mayor o igual a 0.")
    .required("El precio es obligatorio."),

  offerPrice: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? null : value
    )
    .nullable()
    .typeError("El precio oferta debe ser un número válido.")
    .min(0, "El precio oferta debe ser mayor o igual a 0.")
    .test(
      "offer-price",
      "El precio oferta no puede ser mayor al precio normal.",
      function validateOfferPrice(offerPrice) {
        const { price } = this.parent;

        if (
          offerPrice === null ||
          offerPrice === undefined ||
          offerPrice === ""
        ) {
          return true;
        }

        return Number(offerPrice) <= Number(price);
      }
    ),

  cost: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? null : value
    )
    .nullable()
    .typeError("El costo debe ser un número válido.")
    .min(0, "El costo debe ser mayor o igual a 0."),

  stock: Yup.number()
    .typeError("El stock debe ser un número válido.")
    .integer("El stock debe ser un número entero.")
    .min(0, "El stock debe ser mayor o igual a 0.")
    .required("El stock es obligatorio."),

  lowStockAlert: Yup.number()
    .typeError("La alerta de stock debe ser un número válido.")
    .integer("La alerta debe ser un número entero.")
    .min(0, "La alerta debe ser mayor o igual a 0.")
    .required("La alerta de stock es obligatoria."),

  enabled: Yup.boolean().required(),

  images: Yup.array()
    .of(Yup.string().trim())
    .default([]),
});

export const validationSchema = Yup.object({
  id: Yup.string()
    .transform((value) => createProductId(value))
    .max(50, "El código no puede superar 50 caracteres."),

  name: Yup.string()
    .trim()
    .required("El nombre del producto es obligatorio.")
    .max(180, "El nombre no puede superar 180 caracteres."),

  slug: Yup.string()
    .transform((value) => createSlug(value))
    .required("El slug o URL del producto es obligatorio.")
    .max(200, "El slug no puede superar 200 caracteres."),

  category: Yup.string()
    .nullable()
    .required("La categoría es obligatoria."),

  brand: Yup.string().nullable(),

  characteristics: Yup.array().of(Yup.string()).default([]),

  description: Yup.string().max(
    5000,
    "La descripción no puede superar 5,000 caracteres."
  ),

  variants: Yup.array()
    .of(variantSchema)
    .min(1, "Debes registrar al menos una variante.")
    .required("Debes registrar al menos una variante.")
    .test(
      "unique-skus",
      "No puedes repetir SKUs dentro del mismo producto.",
      (variants) => {
        if (!Array.isArray(variants)) {
          return true;
        }

        const skus = variants
          .map((variant) => createSku(variant?.sku))
          .filter(Boolean);

        return new Set(skus).size === skus.length;
      }
    ),

  visible: Yup.boolean().required(),

  featured: Yup.boolean().required(),

  seo: Yup.object({
    metaTitle: Yup.string().max(
      60,
      "El título SEO no puede superar 60 caracteres."
    ),

    metaDescription: Yup.string().max(
      160,
      "La descripción meta no puede superar 160 caracteres."
    ),
  }),

  images: Yup.array()
    .of(
      Yup.object().test(
        "image-shape",
        "Selecciona únicamente archivos de imagen válidos (máx. 5 MB).",
        (image) => {
          if (!image) {
            return true;
          }

          if (image.existing) {
            return Boolean(image.path);
          }

          const file = image.file;

          if (!(file instanceof File)) {
            return false;
          }

          if (!file.type.startsWith("image/")) {
            return false;
          }

          return file.size <= 5 * 1024 * 1024;
        }
      )
    )
    .max(40, "Puedes subir un máximo de 40 imágenes.")
    .default([]),
});